import { ICONS } from 'consts/consts';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Badge,
  Button,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import FilterablePeopleTable, {
  TALBE_SIZE_NO_LIMIT,
} from 'views/Widgets/People/FilterablePeopleTable';

import { FORMAT_TEXT_ONLY } from 'views/Widgets/People/Filters/common';
import { MetadataItem, SkillMetadataItem } from './useAgentMetadata';

interface Skill {
  id: string;
  name: string;
  description?: string;
}

interface SkillSelectorProps {
  value?: Skill[];
  onChange?: (event) => void;
  name?: string;
  availableSkills?: Skill[];
  skillsMetadata?: SkillMetadataItem[];
  resourcesMetadata?: MetadataItem[];
}

const SkillSelector: React.FC<SkillSelectorProps> = ({
  value,
  onChange,
  name,
  availableSkills = [],
  skillsMetadata = [],
  resourcesMetadata = [],
}) => {
  const { formatMessage } = useIntl();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSkillsSet, setSelectedSkillsSet] = useState<Set<string>>(
    new Set()
  );
  const [expandedSkillId, setExpandedSkillId] = useState<string | null>(null);

  // Initialize selected skills set when value changes
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      setSelectedSkillsSet(new Set(value.map((skill) => skill.id)));
    } else {
      setSelectedSkillsSet(new Set());
    }
  }, [value]);

  const toggleModal = useCallback(() => {
    if (!isModalOpen) {
      // Reset selection to current value when opening modal
      if (Array.isArray(value) && value.length > 0) {
        setSelectedSkillsSet(new Set(value.map((skill) => skill.id)));
      } else {
        setSelectedSkillsSet(new Set());
      }
    }
    setIsModalOpen((prev) => !prev);
  }, [isModalOpen, value]);

  const toggleSelectedRowField = useCallback((rowId: string) => {
    setSelectedSkillsSet((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const handleSave = useCallback(() => {
    if (!onChange || !name) {
      return;
    }

    const selectedSkills = availableSkills.filter((skill) =>
      selectedSkillsSet.has(skill.id)
    );

    onChange(selectedSkills);

    setIsModalOpen(false);
  }, [onChange, name, selectedSkillsSet, availableSkills]);

  // Handle various value types - form might pass object, array, null, or undefined
  const currentValue = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  // Build a map of resource IDs to names for display
  const resourceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of resourcesMetadata) {
      map[r.id] = r.name;
    }
    return map;
  }, [resourcesMetadata]);

  // Build a map of internal_tool_name → resource name for instruction interpolation
  const toolNameToResourceName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of resourcesMetadata) {
      if (r.internal_tool_name) {
        map[r.internal_tool_name] = r.name;
      }
    }
    return map;
  }, [resourcesMetadata]);

  // Build a map of skill ID → metadata for quick lookup
  const skillsMetadataMap = useMemo(() => {
    const map: Record<string, SkillMetadataItem> = {};
    for (const s of skillsMetadata) {
      map[s.id] = s;
    }
    return map;
  }, [skillsMetadata]);

  // Render instructions text, replacing internal tool names with resource badges
  const renderInstructions = useCallback(
    (text: string) => {
      const toolNames = Object.keys(toolNameToResourceName);
      if (toolNames.length === 0) {
        return text;
      }
      // Build regex matching any tool name, optionally wrapped in quotes
      const sorted = [...toolNames].sort((a, b) => b.length - a.length);
      const pattern = new RegExp(`['"\`]?(${sorted.join('|')})['"\`]?`, 'g');
      const result: React.ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = pattern.exec(text)) !== null) {
        const toolName = match[1];
        const start = match.index;
        if (start > lastIndex) {
          result.push(text.slice(lastIndex, start));
        }
        result.push(
          <Badge key={start} color="light" className="px-2 py-1 mx-1">
            <i className="fe fe-database me-1" />
            {toolNameToResourceName[toolName]}
          </Badge>
        );
        lastIndex = start + match[0].length;
      }
      if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
      }
      return result;
    },
    [toolNameToResourceName]
  );

  const toggleExpandedSkill = useCallback((skillId: string) => {
    setExpandedSkillId((prev) => (prev === skillId ? null : skillId));
  }, []);

  // Enrich selected skills with metadata for unified table rows
  const enrichedRows = useMemo(() => {
    return currentValue.map((skill) => {
      const meta = skillsMetadataMap[skill.id];
      return {
        ...skill,
        required_resources: meta?.required_resources ?? [],
        instructions: meta?.instructions ?? '',
      };
    });
  }, [currentValue, skillsMetadataMap]);

  // Render a single enriched skill row with expandable instructions
  const renderSkillRow = useCallback(
    (row: (typeof enrichedRows)[number]) => {
      const isExpanded = expandedSkillId === row.id;
      return (
        <div key={row.id} className="list-group-item p-0">
          <div
            className="d-flex align-items-center px-3 py-2"
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => toggleExpandedSkill(row.id)}
          >
            <div className="flex-grow-1">
              <div className="d-flex gap-3">
                <strong style={{ minWidth: '220px', flexShrink: 0 }}>
                  {row.name}
                </strong>
                <div>
                  <span className="text-muted small">{row.description}</span>
                  {row.required_resources.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-1">
                      {row.required_resources.map((rId) => (
                        <Badge key={rId} color="light" className="px-2 py-1">
                          <i className="fe fe-database me-1" />
                          {resourceNameMap[rId] || rId}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <i
              className={`fe fe-chevron-${
                isExpanded ? 'up' : 'down'
              } ms-3 text-muted`}
            />
          </div>
          <Collapse isOpen={isExpanded}>
            <div className="px-3 pb-3">
              <pre
                className="small mb-0 p-2 bg-light rounded"
                style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
              >
                {renderInstructions(row.instructions)}
              </pre>
            </div>
          </Collapse>
        </div>
      );
    },
    [expandedSkillId, toggleExpandedSkill, resourceNameMap, renderInstructions]
  );

  const getUniqueRowId = useCallback((row: Skill) => row.id, []);

  // State for expanded skill in the modal
  const [expandedModalSkillId, setExpandedModalSkillId] = useState<
    string | null
  >(null);
  const toggleExpandedModalSkill = useCallback((skillId: string) => {
    setExpandedModalSkillId((prev) => (prev === skillId ? null : skillId));
  }, []);

  // Columns for the modal with checkbox support
  const modalColumns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin.skill_selector.table.name',
          defaultMessage: 'Skill Name',
        }),
        field: 'person',
        className: 'col-auto',
        sortable: false,
        format: FORMAT_TEXT_ONLY,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin.skill_selector.table.description',
          defaultMessage: 'Description',
        }),
        field: 'detail',
        className: 'col',
        sortable: false,
        format: (
          _val: unknown,
          _uniqueId: string,
          _rowIndex: number,
          row: {
            id: string;
            description?: string;
            required_resources: string[];
            instructions: string;
          }
        ) => {
          const isExpanded = expandedModalSkillId === row.id;
          return (
            <div>
              <span className="text-muted small">{row.description}</span>
              {row.required_resources.length > 0 && (
                <div className="mt-2 d-flex flex-wrap gap-1">
                  {row.required_resources.map((rId) => (
                    <Badge key={rId} color="light" className="px-2 py-1">
                      <i className="fe fe-database me-1" />
                      {resourceNameMap[rId] || rId}
                    </Badge>
                  ))}
                </div>
              )}
              {row.instructions && (
                <>
                  <div className="mt-2">
                    <Button
                      color="link"
                      size="sm"
                      className="text-muted p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpandedModalSkill(row.id);
                      }}
                    >
                      <i
                        className={`fe fe-chevron-${
                          isExpanded ? 'up' : 'down'
                        } me-1`}
                      />
                      <FormattedMessage
                        id="app.views.ai.agents.agents_admin.skill_selector.toggle_instructions"
                        defaultMessage="{action} instructions"
                        values={{
                          action: isExpanded ? 'Hide' : 'Show',
                        }}
                      />
                    </Button>
                  </div>
                  <Collapse isOpen={isExpanded}>
                    <pre
                      className="small mb-0 mt-1 p-2 bg-light rounded"
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                      }}
                    >
                      {renderInstructions(row.instructions)}
                    </pre>
                  </Collapse>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [
      formatMessage,
      resourceNameMap,
      expandedModalSkillId,
      toggleExpandedModalSkill,
      renderInstructions,
    ]
  );

  // Transform skills to include metadata and a fake person object for checkbox rendering
  const modalRows = useMemo(() => {
    return availableSkills.map((skill) => {
      const meta = skillsMetadataMap[skill.id];
      return {
        ...skill,
        required_resources: meta?.required_resources ?? [],
        instructions: meta?.instructions ?? '',
        detail: null,
        person: {
          id: skill.id,
          full_name: skill.name,
          avatar: null,
        },
      };
    });
  }, [availableSkills, skillsMetadataMap]);

  return (
    <>
      <div className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Button color="primary" onClick={toggleModal} size="sm">
            <i className={ICONS.RESUME + ' me-2'} />
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.skill_selector.edit_skills"
              defaultMessage="Edit Skills"
            />
          </Button>
          {currentValue.length > 0 && (
            <span className="text-muted small">
              <FormattedMessage
                id="app.views.ai.agents.agents_admin.skill_selector.selected_count"
                defaultMessage="{count} {count, plural, one {skill} other {skills}} selected"
                values={{ count: currentValue.length }}
              />
            </span>
          )}
        </div>
      </div>

      <div>
        {currentValue.length === 0 ? (
          <p className="text-muted small">
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.skill_selector.no_skills"
              defaultMessage="No skills selected. Click 'Edit Skills' to select skills."
            />
          </p>
        ) : (
          <div className="list-group">{enrichedRows.map(renderSkillRow)}</div>
        )}
      </div>

      <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>
          <FormattedMessage
            id="app.views.ai.agents.agents_admin.skill_selector.modal.title"
            defaultMessage="Select Skills"
          />
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.skill_selector.modal.description"
              defaultMessage="Select skills by clicking the checkboxes on the left. Skills are instruction sets that guide the agent through multi-step tasks."
            />
          </p>
          <FilterablePeopleTable
            rows={modalRows}
            columns={modalColumns}
            hideFilters={false}
            hideExportButton={true}
            initialTableSize={TALBE_SIZE_NO_LIMIT}
            toggleSelectedRowField={toggleSelectedRowField}
            getUniqueRowId={getUniqueRowId}
            selectedRowToggleFieldsSet={selectedSkillsSet}
            setSelectedRowToggleFieldsSet={setSelectedSkillsSet}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.skill_selector.modal.cancel"
              defaultMessage="Cancel"
            />
          </Button>
          <Button color="primary" onClick={handleSave}>
            <FormattedMessage
              id="app.views.ai.agents.agents_admin.skill_selector.modal.save"
              defaultMessage="Save"
            />
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SkillSelector;
